import { useApolloClient } from "@apollo/client";
import { zodResolver } from "apps/web/src/utils/zodResolver";
import { Box, Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import { parseDate, serializeDate } from "@notemeal/utils-date-time";
import ConfirmationDialog from "apps/web/src/componentLibrary/ConfirmationDialog/ConfirmationDialog";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import { getDay } from "date-fns";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import DiscardChangesDialog from "../../../components/universal/DiscardChangesDialog";
import LoadingBackdrop from "../../../components/universal/LoadingBackdrop";
import {
  PlannedMenuGridDocument,
  PlannedMenuGridQuery,
  PlannedMenuGridQueryVariables,
  PlannedMenuTableItemFragment,
} from "../../../types";
import { formatDroppedWeeksMessage, getDroppedWeeks, weekHasContent } from "./droppedWeeks";
import { PlannedMenuForm } from "./PlannedMenuForm";
import { PlannedMenuFormSchema, PlannedMenuFormType, plannedMenuFormDefaultValues } from "./PlannedMenuFormSchema";
import { usePlannedMenuPlannedMenuMutation } from "./PlannedMenusFormProvider";

interface PlannedMenuDuplicateDialogProps {
  open: boolean;
  onClose: () => void;
  menuToDuplicate: PlannedMenuTableItemFragment;
}

export const PlannedMenuDuplicateDialog = ({ open, onClose, menuToDuplicate }: PlannedMenuDuplicateDialogProps) => {
  const [discardChangesOpen, setDiscardChangesOpen] = useState(false);
  // Default the week-count selector to the original menu's week count so the
  // pre-filled (read-only) end date matches the original's end date out of the box.
  const [selectedWeeks, setSelectedWeeks] = useState(menuToDuplicate.weeks.length);
  const { duplicatePlannedMenu, duplicatePlannedMenuCalled } = usePlannedMenuPlannedMenuMutation();
  const apolloClient = useApolloClient();

  // Holds the form values + the non-empty dropped week numbers while the
  // content-loss confirmation dialog is open. null when no confirmation is pending.
  const [pendingConfirmation, setPendingConfirmation] = useState<{
    menu: PlannedMenuFormType;
    droppedNonEmptyWeeks: number[];
  } | null>(null);
  // True while the per-week content queries fired on Save are in flight, to
  // disable Save and avoid double-submits.
  const [checkingDroppedWeeks, setCheckingDroppedWeeks] = useState(false);

  // when duplicating the user must select the same day of the week as the original menu
  // so the duplicated menu doesn't leave meals stranded on disabled days due to a different start day
  const shouldDisableStartDate = (date: Date) => {
    return getDay(date) !== getDay(parseDate(menuToDuplicate.startDate));
  };

  const form = useForm<PlannedMenuFormType>({
    defaultValues: {
      ...plannedMenuFormDefaultValues({ ...menuToDuplicate }, getDay(parseDate(menuToDuplicate.startDate))),
      menuName: `[Copy of] ${menuToDuplicate.name}`,
    },
    resolver: zodResolver(PlannedMenuFormSchema),
    mode: "onChange",
  });

  const { isDirty, isValid } = form.formState;

  const handleClose = () => {
    if (isDirty) {
      setDiscardChangesOpen(true);
    } else {
      onClose();
    }
  };

  const runDuplicate = (newMenu: PlannedMenuFormType) => {
    duplicatePlannedMenu({
      variables: {
        input: {
          originalMenuId: menuToDuplicate.id,
          name: newMenu.menuName,
          description: newMenu.menuDescription,
          startDate: serializeDate(newMenu.dateRange.startDate),
          endDate: serializeDate(newMenu.dateRange.endDate),
          timezone: newMenu.timezone,
        },
      },
    });
  };

  // When the dietitian reduces the week count, the trailing dropped weeks may
  // already contain food items that would be lost. Before duplicating we query
  // the meal content of each dropped week (in parallel) and, if any are
  // non-empty, gate the duplicate behind an explicit confirmation.
  const submit = async (newMenu: PlannedMenuFormType) => {
    // Mirror the server's positional drop logic: the trailing original weeks
    // (by ascending week order) beyond the selected count are the ones lost.
    const droppedWeeks = getDroppedWeeks(selectedWeeks, menuToDuplicate.weeks);

    if (droppedWeeks.length === 0) {
      runDuplicate(newMenu);
      return;
    }

    setCheckingDroppedWeeks(true);
    try {
      const results = await Promise.all(
        droppedWeeks.map(async ({ week, id }) => {
          const { data } = await apolloClient.query<PlannedMenuGridQuery, PlannedMenuGridQueryVariables>({
            query: PlannedMenuGridDocument,
            variables: { plannedMenuWeekId: id },
          });
          return { week, hasContent: weekHasContent(data?.plannedMenuMealsForWeek ?? []) };
        })
      );
      const droppedNonEmptyWeeks = results.filter(r => r.hasContent).map(r => r.week);

      if (droppedNonEmptyWeeks.length === 0) {
        runDuplicate(newMenu);
        return;
      }

      setPendingConfirmation({ menu: newMenu, droppedNonEmptyWeeks });
    } finally {
      setCheckingDroppedWeeks(false);
    }
  };

  const handleConfirmDropWeeks = () => {
    if (pendingConfirmation) {
      runDuplicate(pendingConfirmation.menu);
    }
    setPendingConfirmation(null);
  };

  const handleCancelDropWeeks = () => {
    // Cancel aborts: no mutation fires and the duplicate dialog stays open and usable.
    setPendingConfirmation(null);
  };

  return duplicatePlannedMenuCalled ? (
    <LoadingBackdrop open={open} onClose={onClose} />
  ) : (
    <Dialog
      PaperProps={{
        sx: { width: "900px", height: "600px" },
      }}
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle title="Duplicate Menu" onClose={handleClose} />
      <DialogContent>
        <PlannedMenuForm
          form={form}
          mode="duplicate"
          weeks={selectedWeeks}
          onWeeksChange={setSelectedWeeks}
          shouldDisableStartDate={shouldDisableStartDate}
        />
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button disabled={!isValid || checkingDroppedWeeks} onClick={form.handleSubmit(submit)}>
            Save
          </Button>
        </Box>
      </DialogActions>
      {discardChangesOpen && (
        <DiscardChangesDialog
          open={discardChangesOpen}
          onClose={() => setDiscardChangesOpen(false)}
          onDiscard={onClose} />
      )}
      {pendingConfirmation && (
        <ConfirmationDialog
          open
          title="Duplicate with fewer weeks"
          message={formatDroppedWeeksMessage(pendingConfirmation.droppedNonEmptyWeeks)}
          confirmLabel="Duplicate"
          variant="containedDestructive"
          onConfirm={handleConfirmDropWeeks}
          onCancel={handleCancelDropWeeks}
        />
      )}
    </Dialog>
  );
};
